<template>
  <!-- eslint-disable vue/valid-v-slot -->
  <div class="accounting-page">
    <header class="accounting-header">
      <div class="accounting-heading">
        <div class="accounting-heading-icon" aria-hidden="true">
          <v-icon size="24" color="#fff">mdi-calculator-variant</v-icon>
        </div>
        <div>
          <div class="accounting-eyebrow">Finanse firmy</div>
          <h1>Księgowość</h1>
          <p>Faktury, koszty i rozliczenie VAT w jednym, uporządkowanym widoku.</p>
        </div>
      </div>

      <div class="accounting-period-card">
        <div class="accounting-period-copy">
          <span>Okres księgowy</span>
          <strong>{{ fMonth ? fMonth + '/' + fYear : 'cały ' + fYear }}</strong>
        </div>
        <div class="accounting-period-fields">
          <v-select v-model="fMonth" :items="monthItems" label="Miesiąc" dense outlined hide-details
            class="accounting-month-select" @change="reloadAll" />
          <v-select v-model="fYear" :items="yearItems" label="Rok" dense outlined hide-details
            class="accounting-year-select" @change="reloadAll" />
        </div>
        <div class="accounting-ksef-state" :class="ksef.configured ? 'is-connected' : 'is-disconnected'">
          <span class="accounting-status-dot" aria-hidden="true"></span>
          <div>
            <small>KSeF</small>
            <strong>{{ ksef.configured ? ('Połączony · ' + ksef.env) : 'Niepołączony' }}</strong>
          </div>
        </div>
      </div>
    </header>

    <section v-if="summary" class="accounting-overview" aria-label="Bilans wybranego miesiąca">
      <article class="accounting-overview-card overview-sales">
        <div class="overview-icon"><v-icon size="20">mdi-trending-up</v-icon></div>
        <div class="overview-copy"><span>Sprzedaż brutto</span><strong>{{ zl(summary.sales.brutto) }}</strong><small>{{ summary.sales.count }} faktur</small></div>
      </article>
      <article class="accounting-overview-card overview-costs">
        <div class="overview-icon"><v-icon size="20">mdi-receipt-text-outline</v-icon></div>
        <div class="overview-copy"><span>Koszty brutto</span><strong>{{ zl(summary.costs.brutto) }}</strong><small>{{ summary.costs.count }} dokumentów</small></div>
      </article>
      <article class="accounting-overview-card overview-vat" :class="{ 'is-credit': summary.vatDue < 0 }">
        <div class="overview-icon"><v-icon size="20">mdi-bank-outline</v-icon></div>
        <div class="overview-copy"><span>{{ summary.vatDue < 0 ? 'Nadwyżka VAT' : 'VAT do zapłaty' }}</span><strong>{{ zl(summary.vatDue) }}</strong><small>{{ summary.vatDue < 0 ? 'do przeniesienia' : 'należny − odliczony' }}</small></div>
      </article>
      <article class="accounting-overview-card overview-result" :class="summary.incomeBalance >= 0 ? 'is-positive' : 'is-negative'">
        <div class="overview-icon"><v-icon size="20">mdi-scale-balance</v-icon></div>
        <div class="overview-copy"><span>Wynik netto</span><strong>{{ zl(summary.incomeBalance) }}</strong><small>sprzedaż − koszty</small></div>
      </article>
    </section>

    <div class="accounting-tabs-shell">
      <v-tabs v-model="tab" background-color="transparent" slider-color="#5478FF" show-arrows>
        <v-tab><v-icon left size="20">mdi-file-document-outline</v-icon>Faktury sprzedaży<span class="tab-count">{{ summary ? summary.sales.count : invoices.length }}</span></v-tab>
        <v-tab><v-icon left size="20">mdi-receipt-text-outline</v-icon>Koszty<span class="tab-count">{{ summary ? summary.costs.count : costs.length }}</span></v-tab>
        <v-tab><v-icon left size="20">mdi-cash-clock</v-icon>Do zapłacenia<span v-if="toPay.unpaidCount" class="tab-count tab-count--alert">{{ toPay.unpaidCount }}</span></v-tab>
        <v-tab><v-icon left size="20">mdi-chart-box-outline</v-icon>Podsumowanie</v-tab>
      </v-tabs>
    </div>

    <!-- ═══ FAKTURY ═══ -->
    <section v-show="tab === 0" class="accounting-content-panel">
      <div class="accounting-section-head">
        <div>
          <span class="accounting-section-eyebrow">Sprzedaż</span>
          <h2>Faktury sprzedaży</h2>
          <p>Dokumenty wystawione w wybranym miesiącu wraz ze statusem płatności i KSeF.</p>
        </div>
        <v-btn color="primary" class="accounting-primary-btn" @click="openInvoiceDialog()">
          <v-icon left>mdi-plus</v-icon>Wystaw fakturę
        </v-btn>
      </div>
      <div class="accounting-toolbar">
        <v-text-field v-model="invSearch" label="Szukaj po numerze, nabywcy lub NIP" dense outlined hide-details
          prepend-inner-icon="mdi-magnify" class="accounting-search"
          @input="debouncedInv" @click:clear="loadInvoices" clearable />
        <div class="accounting-result-count">
          <v-icon size="19">mdi-file-document-multiple-outline</v-icon>
          <span><b>{{ invoices.length }}</b> dokumentów na liście</span>
        </div>
      </div>
      <div class="accounting-table-shell accounting-desktop-table">
      <v-data-table :headers="invHeaders" :items="invoices" :loading="loadingInv"
        :items-per-page="25" class="accounting-table"
        no-data-text="Brak faktur w wybranym okresie" loading-text="Pobieram faktury…"
        :footer-props="{ 'items-per-page-options': [25, 50, 100] }">
        <template v-slot:item.number="{ item }">
          <div class="accounting-primary-cell">
            <strong>{{ item.number }}</strong>
            <v-chip v-if="item.autoImported" x-small outlined color="indigo">z KSeF</v-chip>
          </div>
        </template>
        <template v-slot:item.buyerName="{ item }">
          <div class="accounting-party-cell"><strong>{{ item.buyerName || '—' }}</strong><small v-if="item.buyerNip">NIP {{ item.buyerNip }}</small></div>
        </template>
        <template v-slot:item.issueDate="{ item }">{{ fmtDate(item.issueDate) }}</template>
        <template v-slot:item.nettoTotal="{ item }"><span class="accounting-money">{{ zl(item.nettoTotal) }}</span></template>
        <template v-slot:item.vatTotal="{ item }"><span class="accounting-money accounting-money--muted">{{ zl(item.vatTotal) }}</span></template>
        <template v-slot:item.bruttoTotal="{ item }"><strong class="accounting-money accounting-money--strong">{{ zl(item.bruttoTotal) }}</strong></template>
        <template v-slot:item.status="{ item }">
          <v-chip small dark class="accounting-status-chip" :color="statusColor(item.status)">{{ statusLabel(item.status) }}</v-chip>
        </template>
        <template v-slot:item.ksefStatus="{ item }">
          <v-tooltip bottom v-if="item.ksefStatus !== 'NONE'">
            <template v-slot:activator="{ on }">
              <v-chip small dark class="accounting-status-chip" v-on="on" :color="ksefColor(item.ksefStatus)">
                {{ ksefLabel(item.ksefStatus) }}
              </v-chip>
            </template>
            <span>{{ item.ksefNumber || item.ksefError || item.ksefRefNo || '—' }}</span>
          </v-tooltip>
          <span v-else class="grey--text">—</span>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="accounting-row-actions">
          <v-btn icon small class="accounting-row-btn is-primary" @click="downloadPdf(item)" title="Pobierz PDF">
            <v-icon small>mdi-file-pdf-box</v-icon>
          </v-btn>
          <v-btn icon small class="accounting-row-btn" @click="sendKsef(item)" :disabled="item.ksefStatus === 'SENT'"
            :title="item.ksefStatus === 'SENT' ? 'Wysłana do KSeF' : 'Wyślij do KSeF'">
            <v-icon small :color="item.ksefStatus === 'SENT' ? 'green' : ''">mdi-bank-transfer-out</v-icon>
          </v-btn>
          <v-btn v-if="item.ksefStatus === 'PENDING'" icon small class="accounting-row-btn" @click="refreshKsef(item)" title="Odśwież status KSeF">
            <v-icon small>mdi-refresh</v-icon>
          </v-btn>
          <v-btn icon small class="accounting-row-btn" @click="togglePaid(item)"
            :title="item.status === 'PAID' ? 'Cofnij opłacenie' : 'Oznacz jako opłaconą'">
            <v-icon small :color="item.status === 'PAID' ? 'green' : ''">mdi-cash-check</v-icon>
          </v-btn>
          <v-btn icon small class="accounting-row-btn" @click="openInvoiceDialog(item)" :disabled="item.ksefStatus === 'SENT'" title="Edytuj">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon small class="accounting-row-btn is-danger" @click="deleteInvoice(item)" :disabled="item.ksefStatus === 'SENT'" title="Usuń">
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
          </div>
        </template>
      </v-data-table>
      </div>

      <div class="accounting-mobile-list">
        <article v-for="item in invoices" :key="item.id" class="accounting-mobile-card">
          <div class="mobile-card-top">
            <div><small>Faktura</small><strong>{{ item.number }}</strong></div>
            <v-chip small dark :color="statusColor(item.status)">{{ statusLabel(item.status) }}</v-chip>
          </div>
          <div class="mobile-card-party">{{ item.buyerName || '—' }}<small v-if="item.buyerNip">NIP {{ item.buyerNip }}</small></div>
          <div class="mobile-card-facts">
            <div><span>Data</span><strong>{{ fmtDate(item.issueDate) }}</strong></div>
            <div><span>Brutto</span><strong>{{ zl(item.bruttoTotal) }}</strong></div>
            <div><span>KSeF</span><strong>{{ item.ksefStatus === 'NONE' ? '—' : ksefLabel(item.ksefStatus) }}</strong></div>
          </div>
          <div class="mobile-card-actions">
            <v-btn small text @click="downloadPdf(item)"><v-icon left small>mdi-file-pdf-box</v-icon>PDF</v-btn>
            <v-btn small text @click="togglePaid(item)"><v-icon left small>mdi-cash-check</v-icon>{{ item.status === 'PAID' ? 'Cofnij' : 'Opłacona' }}</v-btn>
            <v-btn small text @click="openInvoiceDialog(item)" :disabled="item.ksefStatus === 'SENT'"><v-icon left small>mdi-pencil</v-icon>Edytuj</v-btn>
            <v-menu offset-y left>
              <template v-slot:activator="{ on, attrs }"><v-btn small icon v-bind="attrs" v-on="on"><v-icon>mdi-dots-horizontal</v-icon></v-btn></template>
              <v-list dense>
                <v-list-item @click="sendKsef(item)" :disabled="item.ksefStatus === 'SENT'"><v-list-item-icon><v-icon small>mdi-bank-transfer-out</v-icon></v-list-item-icon><v-list-item-title>Wyślij do KSeF</v-list-item-title></v-list-item>
                <v-list-item v-if="item.ksefStatus === 'PENDING'" @click="refreshKsef(item)"><v-list-item-icon><v-icon small>mdi-refresh</v-icon></v-list-item-icon><v-list-item-title>Odśwież KSeF</v-list-item-title></v-list-item>
                <v-list-item @click="deleteInvoice(item)" :disabled="item.ksefStatus === 'SENT'"><v-list-item-icon><v-icon small color="error">mdi-delete</v-icon></v-list-item-icon><v-list-item-title>Usuń</v-list-item-title></v-list-item>
              </v-list>
            </v-menu>
          </div>
        </article>
        <div v-if="!loadingInv && !invoices.length" class="accounting-empty-state"><v-icon size="32">mdi-file-document-outline</v-icon><strong>Brak faktur</strong><span>Nie znaleziono dokumentów w tym okresie.</span></div>
      </div>
    </section>

    <!-- ═══ KOSZTY ═══ -->
    <section v-show="tab === 1" class="accounting-content-panel">
      <div class="accounting-section-head accounting-section-head--costs">
        <div>
          <span class="accounting-section-eyebrow">Wydatki</span>
          <h2>Koszty firmy</h2>
          <p>Faktury otrzymane, odliczenia VAT i dokumenty wymagające sprawdzenia.</p>
          <div v-if="ksef.configured" class="accounting-automation-note">
            <v-icon size="17">mdi-autorenew</v-icon><span>Automatyczny import z KSeF codziennie o 3:30</span>
          </div>
        </div>
        <div class="accounting-section-actions">
          <v-btn outlined color="primary" :loading="importing" @click="openImportDialog" v-if="ksef.configured">
            <v-icon left>mdi-bank-transfer-in</v-icon>Importuj KSeF
          </v-btn>
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn outlined v-bind="attrs" v-on="on">
                <v-icon left>mdi-archive-arrow-down</v-icon>Archiwum<v-icon right small>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="downloadZip">
                <v-list-item-icon class="mr-2"><v-icon small>mdi-folder-zip</v-icon></v-list-item-icon>
                <v-list-item-title>Pobierz ZIP faktur ({{ fMonth ? fMonth + '/' + fYear : 'cały ' + fYear }})</v-list-item-title>
              </v-list-item>
              <v-list-item @click="openKsefIncoming">
                <v-list-item-icon class="mr-2"><v-icon small>mdi-bank-transfer-in</v-icon></v-list-item-icon>
                <v-list-item-title>Przeglądaj KSeF ręcznie…</v-list-item-title>
              </v-list-item>
              <v-list-item @click="clearXmls">
                <v-list-item-icon class="mr-2"><v-icon small color="red">mdi-delete-sweep</v-icon></v-list-item-icon>
                <v-list-item-title>Usuń XML-e z serwera ({{ fMonth ? fMonth + '/' + fYear : 'cały ' + fYear }})</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-btn color="primary" class="accounting-primary-btn" @click="openCostDialog()">
            <v-icon left>mdi-plus</v-icon>Dodaj koszt
          </v-btn>
        </div>
      </div>

      <v-dialog v-model="importDialog" max-width="480" content-class="dark-form-dialog">
        <v-card class="accounting-dialog">
          <v-card-title><v-icon left color="primary">mdi-bank-transfer-in</v-icon>Import z KSeF</v-card-title>
          <v-card-text>
            <div class="accounting-dialog-hint">Import obejmie koszty i sprzedaż. Duplikaty są pomijane, więc ten sam zakres możesz pobrać ponownie.</div>
            <v-row dense>
              <v-col cols="12" sm="6"><v-text-field v-model="importFrom" label="Od" type="date" dense outlined hide-details /></v-col>
              <v-col cols="12" sm="6"><v-text-field v-model="importTo" label="Do" type="date" dense outlined hide-details /></v-col>
            </v-row>
            <div class="accounting-range-shortcuts">
              <v-chip small outlined @click="setImportRange(7)">Ostatnie 7 dni</v-chip>
              <v-chip small outlined @click="setImportRange(31)">Ostatni miesiąc</v-chip>
              <v-chip small outlined @click="setImportRange(365)">Ostatni rok</v-chip>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="importDialog = false">Anuluj</v-btn>
            <v-btn color="primary" :loading="importing" @click="importNow">Importuj</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <div class="accounting-toolbar accounting-toolbar--costs">
        <v-text-field v-model="costSearch" label="Szukaj po numerze, kontrahencie, NIP lub opisie" dense outlined hide-details
          prepend-inner-icon="mdi-magnify" class="accounting-search"
          @input="debouncedCosts" @click:clear="loadCosts" clearable />
        <v-select v-model="costCategory" :items="[{ text: 'Wszystkie kategorie', value: '' }, ...categories]"
          label="Kategoria" dense outlined hide-details class="accounting-category-select" @change="loadCosts" />
        <div class="accounting-result-count">
          <v-icon size="19">mdi-receipt-text-outline</v-icon>
          <span><b>{{ costs.length }}</b> dokumentów na liście</span>
        </div>
      </div>
      <div class="accounting-table-shell accounting-desktop-table">
      <v-data-table :headers="costHeaders" :items="costs" :loading="loadingCosts"
        :items-per-page="25" class="accounting-table accounting-table--costs"
        no-data-text="Brak kosztów w wybranym okresie" loading-text="Pobieram koszty…"
        :footer-props="{ 'items-per-page-options': [25, 50, 100] }">
        <template v-slot:item.number="{ item }"><strong class="accounting-document-number">{{ item.number }}</strong></template>
        <template v-slot:item.contractorName="{ item }"><div class="accounting-party-cell"><strong>{{ item.contractorName || '—' }}</strong><small v-if="item.contractorNip">NIP {{ item.contractorNip }}</small></div></template>
        <template v-slot:item.issueDate="{ item }">{{ fmtDate(item.issueDate) }}</template>
        <template v-slot:item.category="{ item }"><span class="accounting-category-pill">{{ item.category }}</span></template>
        <template v-slot:item.netto="{ item }"><span class="accounting-money">{{ zl(item.netto) }}</span></template>
        <template v-slot:item.vat="{ item }"><span class="accounting-money accounting-money--muted">{{ zl(item.vat) }}</span></template>
        <template v-slot:item.brutto="{ item }"><strong class="accounting-money accounting-money--strong">{{ zl(item.brutto) }}</strong></template>
        <template v-slot:item.deduction="{ item }">
          <v-chip x-small dark :color="dedColor(item.deduction)">{{ dedLabel(item.deduction) }}</v-chip>
        </template>
        <template v-slot:item.vatDeductible="{ item }">
          <span class="accounting-money accounting-money--positive">{{ zl(item.vatDeductible) }}</span>
        </template>
        <template v-slot:item.paid="{ item }">
          <span class="accounting-inline-status" :class="item.paid ? 'is-success' : 'is-muted'"><v-icon small>{{ item.paid ? 'mdi-check-circle' : 'mdi-clock-outline' }}</v-icon>{{ item.paid ? 'tak' : 'nie' }}</span>
        </template>
        <template v-slot:item.verified="{ item }">
          <v-tooltip bottom v-if="!item.verified">
            <template v-slot:activator="{ on }">
              <v-chip x-small color="orange" dark v-on="on">do weryfikacji</v-chip>
            </template>
            <span>Import z KSeF — sprawdź kategorię i odliczenie VAT (edytuj i zapisz)</span>
          </v-tooltip>
          <span v-else class="accounting-inline-status is-success"><v-icon small>mdi-check-circle</v-icon>sprawdzona</span>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="accounting-row-actions">
          <v-btn icon small class="accounting-row-btn is-primary" v-if="item.hasXml || item.ksefNumber" @click="openCostPreview(item)" title="Podgląd faktury">
            <v-icon small>mdi-eye</v-icon>
          </v-btn>
          <v-btn icon small class="accounting-row-btn" :title="item.toPay ? 'Zdejmij z listy do zapłaty' : 'Oznacz: do zapłaty'" @click="toggleToPay(item, !item.toPay)">
            <v-icon small :color="item.toPay ? 'orange' : ''">{{ item.toPay ? 'mdi-cash-clock' : 'mdi-cash-plus' }}</v-icon>
          </v-btn>
          <v-btn icon small class="accounting-row-btn" v-if="item.contractorNip" title="Oznacz adresata — jego faktury zawsze do zapłaty" @click="openPayeeDialog(null, item)">
            <v-icon small>mdi-account-cash</v-icon>
          </v-btn>
          <v-btn icon small class="accounting-row-btn" @click="openCostDialog(item)" title="Edytuj"><v-icon small>mdi-pencil</v-icon></v-btn>
          <v-btn icon small class="accounting-row-btn is-danger" @click="deleteCost(item)" title="Usuń"><v-icon small>mdi-delete</v-icon></v-btn>
          </div>
        </template>
      </v-data-table>
      </div>

      <div class="accounting-mobile-list">
        <article v-for="item in costs" :key="item.id" class="accounting-mobile-card">
          <div class="mobile-card-top">
            <div><small>Koszt</small><strong>{{ item.contractorName || '—' }}</strong></div>
            <span class="accounting-category-pill">{{ item.category }}</span>
          </div>
          <div class="mobile-card-party">{{ item.number }}<small>{{ fmtDate(item.issueDate) }}<span v-if="item.contractorNip"> · NIP {{ item.contractorNip }}</span></small></div>
          <div class="mobile-card-facts">
            <div><span>Brutto</span><strong>{{ zl(item.brutto) }}</strong></div>
            <div><span>VAT do odliczenia</span><strong class="is-positive-text">{{ zl(item.vatDeductible) }}</strong></div>
            <div><span>Status</span><strong>{{ item.paid ? 'Opłacona' : 'Nieopłacona' }}</strong></div>
          </div>
          <div v-if="!item.verified" class="mobile-verification-note"><v-icon size="18">mdi-alert-circle-outline</v-icon>Wymaga sprawdzenia kategorii i VAT</div>
          <div class="mobile-card-actions">
            <v-btn v-if="item.hasXml || item.ksefNumber" small text @click="openCostPreview(item)"><v-icon left small>mdi-eye</v-icon>Podgląd</v-btn>
            <v-btn small text @click="openCostDialog(item)"><v-icon left small>mdi-pencil</v-icon>Edytuj</v-btn>
            <v-btn small text class="is-danger-text" @click="deleteCost(item)"><v-icon left small>mdi-delete</v-icon>Usuń</v-btn>
          </div>
        </article>
        <div v-if="!loadingCosts && !costs.length" class="accounting-empty-state"><v-icon size="32">mdi-receipt-text-outline</v-icon><strong>Brak kosztów</strong><span>Nie znaleziono dokumentów w tym okresie.</span></div>
      </div>

      <!-- ═══ DIALOG: PODGLĄD FAKTURY (dane z XML KSeF) ═══ -->
      <v-dialog v-model="previewDialog" max-width="860" scrollable content-class="dark-form-dialog">
        <v-card v-if="preview" class="accounting-dialog accounting-preview-dialog">
          <v-card-title class="accounting-preview-title">
            <div><small>Podgląd dokumentu</small><strong>Faktura {{ preview.invoice ? preview.invoice.number : preview.cost.number }}</strong></div>
            <v-chip small color="indigo" dark v-if="preview.cost.ksefNumber">KSeF: {{ preview.cost.ksefNumber }}</v-chip>
            <div class="accounting-preview-actions">
            <v-btn small outlined v-if="preview.hasXml" @click="downloadCostPdf(preview.cost)">
              <v-icon small left>mdi-file-pdf-box</v-icon>PDF
            </v-btn>
            <v-btn small outlined @click="downloadCostXml(preview.cost)" v-if="preview.hasXml">
              <v-icon small left>mdi-xml</v-icon>XML
            </v-btn>
            </div>
          </v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" sm="6">
                <div class="text-caption grey--text">Sprzedawca</div>
                <div class="font-weight-bold">{{ preview.seller && preview.seller.name || '—' }}</div>
                <div v-if="preview.seller && preview.seller.nip">NIP: {{ preview.seller.nip }}</div>
                <div v-if="preview.seller && preview.seller.address" class="text-caption">{{ preview.seller.address }}</div>
              </v-col>
              <v-col cols="12" sm="6" v-if="preview.buyer">
                <div class="text-caption grey--text">Nabywca</div>
                <div class="font-weight-bold">{{ preview.buyer.name || '—' }}</div>
                <div v-if="preview.buyer.nip">NIP: {{ preview.buyer.nip }}</div>
              </v-col>
            </v-row>
            <v-row dense class="mt-1" v-if="preview.invoice">
              <v-col cols="auto"><span class="grey--text">Wystawiona:</span> {{ preview.invoice.issueDate || '—' }}</v-col>
              <v-col cols="auto" v-if="preview.invoice.saleDate"><span class="grey--text">Sprzedaż:</span> {{ preview.invoice.saleDate }}</v-col>
              <v-col cols="auto" v-if="preview.invoice.dueDate"><span class="grey--text">Termin płatności:</span> {{ preview.invoice.dueDate }}</v-col>
            </v-row>

            <div v-if="preview.extras.length" class="mt-3 pa-3 rounded amber lighten-5" style="border:1px solid #f0c36d">
              <div class="font-weight-bold mb-1">📌 Pola dodatkowe (tu zwykle nr rejestracyjny)</div>
              <div v-for="(e, i) in preview.extras" :key="i"><b>{{ e.key }}:</b> {{ e.value }}</div>
            </div>

            <div class="font-weight-bold mt-4 mb-1" v-if="preview.items.length">Pozycje</div>
            <div class="accounting-simple-table-wrap" v-if="preview.items.length">
            <v-simple-table dense>
              <thead><tr><th>Lp</th><th>Nazwa</th><th class="text-right">Ilość</th><th>Jm</th>
                <th class="text-right">Cena netto</th><th class="text-right">Netto</th><th class="text-right">VAT</th></tr></thead>
              <tbody>
                <tr v-for="it in preview.items" :key="it.lp">
                  <td>{{ it.lp }}</td><td>{{ it.name }}</td>
                  <td class="text-right">{{ it.quantity || '—' }}</td><td>{{ it.unit || '—' }}</td>
                  <td class="text-right">{{ it.unitPrice || '—' }}</td>
                  <td class="text-right">{{ it.netto || '—' }}</td>
                  <td class="text-right">{{ it.vatRate }}%</td>
                </tr>
              </tbody>
            </v-simple-table>
            </div>
            <div v-else-if="!preview.hasXml" class="grey--text mt-2">
              Brak XML-a faktury (koszt dodany ręcznie albo KSeF nie udostępnia pliku).
            </div>

            <div v-if="preview.footer.length" class="mt-3">
              <div class="font-weight-bold mb-1">Uwagi ze stopki</div>
              <div v-for="(f2, i) in preview.footer" :key="i" class="text-caption">{{ f2 }}</div>
            </div>
            <div v-if="preview.bankAccounts.length" class="mt-2 text-caption">
              <b>Rachunek bankowy:</b> {{ preview.bankAccounts.join(', ') }}
            </div>

            <div class="accounting-preview-total">
              <div>
                <div>Netto: <b>{{ zl(preview.cost.netto) }}</b> · VAT: <b>{{ zl(preview.cost.vat) }}</b></div>
                <div class="text-h6">Brutto: <b>{{ zl(preview.cost.brutto) }}</b></div>
                <div class="text-caption green--text text--darken-2">
                  VAT do odliczenia ({{ dedLabel(preview.cost.deduction) }}): {{ zl(preview.cost.vatDeductible) }}
                </div>
              </div>
            </div>
          </v-card-text>
          <v-card-actions><v-spacer /><v-btn text @click="previewDialog = false">Zamknij</v-btn></v-card-actions>
        </v-card>
        <v-card v-else class="accounting-dialog pa-6 text-center"><v-progress-circular indeterminate color="primary" /></v-card>
      </v-dialog>
    </section>

    <!-- ═══ PODSUMOWANIE ═══ -->
    <!-- ══ DO ZAPŁACENIA ══ -->
    <section v-show="tab === 2" class="accounting-content-panel">
      <div class="accounting-section-head">
        <div>
          <span class="accounting-section-eyebrow">Płatności</span>
          <h2>Faktury do zapłacenia</h2>
          <p>Oznaczone ręcznie oraz wszystkie faktury adresatów z listy poniżej (auto przy imporcie z KSeF).</p>
        </div>
        <div class="accounting-section-actions">
          <v-btn outlined color="primary" @click="openPayeeDialog()">
            <v-icon left>mdi-account-cash</v-icon>Dodaj adresata
          </v-btn>
          <v-btn color="primary" class="accounting-primary-btn" :disabled="!toPay.unpaidCount" @click="basketDialog = true">
            <v-icon left>mdi-bank-transfer-out</v-icon>Generuj koszyk przelewów
          </v-btn>
        </div>
      </div>

      <div class="accounting-toolbar">
        <div class="accounting-result-count">
          <v-icon size="19" color="orange">mdi-cash-clock</v-icon>
          <span><b>{{ toPay.unpaidCount }}</b> nieopłaconych na <b>{{ zl(toPay.unpaidBrutto) }}</b></span>
        </div>
        <div class="accounting-payees" v-if="payees.length">
          <span class="accounting-payees-label">Adresaci:</span>
          <v-chip v-for="p in payees" :key="p.id" small close outlined class="mr-1 mb-1"
            :title="'NIP ' + p.contractorNip + (p.accountNo ? ' · rachunek zapisany' : ' · brak rachunku')"
            @click="openPayeeDialog(p)" @click:close="removePayee(p)">
            <v-icon v-if="p.accountNo" x-small left color="green">mdi-bank-check</v-icon>{{ p.contractorName }}
          </v-chip>
        </div>
      </div>

      <div class="accounting-table-shell">
        <v-data-table :headers="toPayHeaders" :items="toPay.costs" :loading="loadingToPay"
          :items-per-page="25" class="accounting-table"
          no-data-text="Nic do zapłacenia — oznacz fakturę w Kosztach albo dodaj adresata"
          :footer-props="{ 'items-per-page-options': [25, 50, 100] }">
          <template v-slot:item.number="{ item }"><strong class="accounting-document-number">{{ item.number }}</strong></template>
          <template v-slot:item.contractorName="{ item }"><div class="accounting-party-cell"><strong>{{ item.contractorName || '—' }}</strong><small v-if="item.contractorNip">NIP {{ item.contractorNip }}</small></div></template>
          <template v-slot:item.issueDate="{ item }">{{ fmtDate(item.issueDate) }}</template>
          <template v-slot:item.brutto="{ item }"><strong class="accounting-money accounting-money--strong">{{ zl(item.brutto) }}</strong></template>
          <template v-slot:item.paid="{ item }">
            <span class="accounting-inline-status" :class="item.paid ? 'is-success' : 'is-muted'">
              <v-icon small>{{ item.paid ? 'mdi-check-circle' : 'mdi-clock-outline' }}</v-icon>
              {{ item.paid ? 'opłacona ' + (item.paidAt ? fmtDate(item.paidAt) : '') : 'do zapłaty' }}
            </span>
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="accounting-row-actions">
              <v-btn icon small class="accounting-row-btn is-primary" v-if="item.hasXml || item.ksefNumber" @click="openCostPreview(item)" title="Podgląd faktury">
                <v-icon small>mdi-eye</v-icon>
              </v-btn>
              <v-btn v-if="!item.paid" icon small class="accounting-row-btn is-success" @click="markCostPaid(item, true)" title="Oznacz jako opłaconą">
                <v-icon small color="green">mdi-check-circle</v-icon>
              </v-btn>
              <v-btn v-else icon small class="accounting-row-btn" @click="markCostPaid(item, false)" title="Cofnij opłacenie">
                <v-icon small>mdi-undo</v-icon>
              </v-btn>
              <v-btn icon small class="accounting-row-btn" @click="toggleToPay(item, false)" title="Zdejmij z listy do zapłaty">
                <v-icon small>mdi-playlist-remove</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </div>

      <!-- dialog adresata -->
      <v-dialog v-model="payeeDialog" max-width="440" content-class="dark-form-dialog">
        <v-card class="accounting-dialog">
          <v-card-title><v-icon left color="primary">mdi-account-cash</v-icon>Adresat do zapłaty</v-card-title>
          <v-card-text>
            <div class="accounting-dialog-hint">Wszystkie faktury tego kontrahenta (po NIP) będą automatycznie trafiać do „Do zapłacenia" — także przyszłe importy z KSeF.</div>
            <v-text-field v-model="payeeForm.contractorName" label="Nazwa kontrahenta" dense outlined />
            <v-text-field v-model="payeeForm.contractorNip" label="NIP" dense outlined />
            <v-text-field v-model="payeeForm.accountNo" label="Rachunek NRB (26 cyfr, opcjonalnie)" dense outlined
              hint="Do koszyka przelewów, gdy faktura z KSeF nie zawiera numeru rachunku" persistent-hint />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="payeeDialog = false">Anuluj</v-btn>
            <v-btn color="primary" :loading="saving" @click="savePayee">Zapisz</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- dialog koszyka -->
      <v-dialog v-model="basketDialog" max-width="480" content-class="dark-form-dialog">
        <v-card class="accounting-dialog">
          <v-card-title><v-icon left color="primary">mdi-bank-transfer-out</v-icon>Koszyk przelewów</v-card-title>
          <v-card-text>
            <div class="accounting-dialog-hint">Plik obejmie wszystkie nieopłacone pozycje z listy. Rachunki odbiorców biorę z faktur KSeF (NrRB) albo z adresata.</div>
            <v-select v-model="basketForm.bankFormat" :items="['MBANK', 'PKO']" label="Format banku" dense outlined />
            <v-text-field v-model="basketForm.senderAccountNo" label="Rachunek nadawcy (26 cyfr)" dense outlined />
            <v-text-field v-model="basketForm.senderName" label="Nazwa nadawcy" dense outlined />
            <v-alert v-if="basketSkipped.length" dense text type="warning" class="mt-2">
              Pominięte (brak rachunku/kwoty):
              <div v-for="s in basketSkipped" :key="s.number">• {{ s.contractorName }} — {{ s.number }} ({{ s.reason }})</div>
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="basketDialog = false">Zamknij</v-btn>
            <v-btn color="primary" :loading="saving" @click="generateBasket">Generuj plik</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </section>

    <section v-show="tab === 3" class="accounting-content-panel accounting-summary-panel">
      <div class="accounting-section-head">
        <div>
          <span class="accounting-section-eyebrow">Bilans miesiąca</span>
          <h2>Podsumowanie księgowe</h2>
          <p>Najważniejsze kwoty oraz rozbicie sprzedaży, VAT i kosztów za {{ fMonth ? fMonth + '/' + fYear : 'bieżący miesiąc (podsumowanie VAT jest zawsze miesięczne)' }}.</p>
        </div>
      </div>
      <v-row dense v-if="summary" class="accounting-summary-grid">
        <v-col cols="12" sm="6" md="4" lg="2" v-for="card in summaryCards" :key="card.label">
          <v-card class="accounting-summary-card" :class="{
            'is-key': card.dark,
            'is-positive': card.color === 'green',
            'is-negative': card.color === 'red'
          }">
            <div class="accounting-summary-icon"><v-icon size="21">{{ card.label === 'VAT do zapłaty' ? 'mdi-bank-outline' : card.label === 'Wynik (netto)' ? 'mdi-scale-balance' : 'mdi-chart-line' }}</v-icon></div>
            <div class="accounting-summary-label">{{ card.label }}</div>
            <div class="accounting-summary-value">{{ card.value }}</div>
            <div class="accounting-summary-sub" v-if="card.sub">{{ card.sub }}</div>
          </v-card>
        </v-col>
      </v-row>

      <v-row dense class="accounting-breakdown-grid" v-if="summary">
        <v-col cols="12" md="4">
          <v-card class="accounting-breakdown-card">
            <div class="accounting-breakdown-head"><div class="breakdown-icon"><v-icon size="20">mdi-chart-line</v-icon></div><div><strong>Sprzedaż według VAT</strong><span>Podział wartości sprzedaży</span></div></div>
            <v-simple-table dense>
              <thead><tr><th>Stawka</th><th class="text-right">Netto</th><th class="text-right">VAT</th></tr></thead>
              <tbody>
                <tr v-for="(v, rate) in summary.sales.byRate" :key="rate">
                  <td>{{ rate }}%</td>
                  <td class="text-right">{{ zl(v.netto) }}</td>
                  <td class="text-right">{{ zl(v.vat) }}</td>
                </tr>
                <tr v-if="!Object.keys(summary.sales.byRate).length"><td colspan="3" class="grey--text">Brak faktur</td></tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card class="accounting-breakdown-card">
            <div class="accounting-breakdown-head"><div class="breakdown-icon"><v-icon size="20">mdi-percent-outline</v-icon></div><div><strong>Odliczenia VAT</strong><span>VAT naliczony i możliwy do odliczenia</span></div></div>
            <v-simple-table dense>
              <thead><tr><th>Odliczenie</th><th class="text-right">VAT z faktur</th><th class="text-right">Do odliczenia</th></tr></thead>
              <tbody>
                <tr v-for="(v, d) in summary.costs.byDeduction" :key="d">
                  <td><v-chip x-small dark :color="dedColor(d)">{{ dedLabel(d) }}</v-chip> ({{ v.count }})</td>
                  <td class="text-right">{{ zl(v.vat) }}</td>
                  <td class="text-right green--text text--darken-2">{{ zl(v.deductible) }}</td>
                </tr>
                <tr v-if="!Object.keys(summary.costs.byDeduction).length"><td colspan="3" class="grey--text">Brak kosztów</td></tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card class="accounting-breakdown-card">
            <div class="accounting-breakdown-head"><div class="breakdown-icon"><v-icon size="20">mdi-shape-outline</v-icon></div><div><strong>Koszty według kategorii</strong><span>Gdzie firma poniosła wydatki</span></div></div>
            <v-simple-table dense>
              <thead><tr><th>Kategoria</th><th class="text-right">Netto</th><th class="text-right">Brutto</th></tr></thead>
              <tbody>
                <tr v-for="(v, cat) in summary.costs.byCategory" :key="cat">
                  <td>{{ cat }} ({{ v.count }})</td>
                  <td class="text-right">{{ zl(v.netto) }}</td>
                  <td class="text-right">{{ zl(v.brutto) }}</td>
                </tr>
                <tr v-if="!Object.keys(summary.costs.byCategory).length"><td colspan="3" class="grey--text">Brak kosztów</td></tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>
    </section>

    <!-- ═══ DIALOG: FAKTURA ═══ -->
    <v-dialog v-model="invDialog" max-width="900" persistent scrollable content-class="dark-form-dialog">
      <v-card class="accounting-dialog">
        <v-card-title class="accounting-dialog-title"><div class="dialog-title-icon"><v-icon>mdi-file-document-edit-outline</v-icon></div><div><strong>{{ invForm.id ? 'Edytuj fakturę' : 'Wystaw fakturę' }}</strong><span>Uzupełnij dane nabywcy, pozycje i termin płatności.</span></div></v-card-title>
        <v-card-text>
          <div class="accounting-form-section-title"><v-icon size="19">mdi-domain</v-icon><span>Nabywca</span></div>
          <v-row dense>
            <v-col cols="12" md="6"><v-text-field v-model="invForm.buyerName" label="Nazwa *" dense outlined hide-details /></v-col>
            <v-col cols="12" sm="6" md="3"><v-text-field v-model="invForm.buyerNip" label="NIP" dense outlined hide-details /></v-col>
            <v-col cols="12" sm="6" md="3"><v-text-field v-model="invForm.buyerEmail" label="E-mail" dense outlined hide-details /></v-col>
            <v-col cols="12" md="6"><v-text-field v-model="invForm.buyerAddress" label="Ulica i numer" dense outlined hide-details /></v-col>
            <v-col cols="12" sm="6" md="3"><v-text-field v-model="invForm.buyerZip" label="Kod pocztowy" dense outlined hide-details /></v-col>
            <v-col cols="12" sm="6" md="3"><v-text-field v-model="invForm.buyerCity" label="Miasto" dense outlined hide-details /></v-col>
          </v-row>
          <div class="accounting-form-section-title mt-4"><v-icon size="19">mdi-calendar-month-outline</v-icon><span>Daty i płatność</span></div>
          <v-row dense>
            <v-col cols="12" sm="6" md="3"><v-text-field v-model="invForm.issueDate" label="Data wystawienia" type="date" dense outlined hide-details /></v-col>
            <v-col cols="12" sm="6" md="3"><v-text-field v-model="invForm.saleDate" label="Data sprzedaży" type="date" dense outlined hide-details /></v-col>
            <v-col cols="12" sm="6" md="3"><v-text-field v-model="invForm.dueDate" label="Termin płatności" type="date" dense outlined hide-details /></v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select v-model="invForm.paymentMethod" :items="['przelew', 'gotówka', 'karta']" label="Forma płatności" dense outlined hide-details />
            </v-col>
          </v-row>
          <div class="accounting-form-section-title accounting-form-section-title--actions mt-4">
            <div><v-icon size="19">mdi-format-list-bulleted</v-icon><span>Pozycje faktury</span></div>
            <v-spacer />
            <v-btn x-small outlined color="primary" @click="invForm.items.push(emptyItem())">
              <v-icon x-small left>mdi-plus</v-icon>Dodaj pozycję
            </v-btn>
          </div>
          <v-row dense v-for="(it, i) in invForm.items" :key="i" align="center" class="accounting-invoice-item">
            <v-col cols="12" md="4"><v-text-field v-model="it.name" :label="`Pozycja ${i + 1} — nazwa *`" dense outlined hide-details /></v-col>
            <v-col cols="6" sm="3" md="1"><v-text-field v-model.number="it.quantity" label="Ilość" type="number" min="0" dense outlined hide-details /></v-col>
            <v-col cols="6" sm="3" md="1">
              <v-select v-model="it.unit" :items="['szt.', 'usł.', 'godz.', 'km', 'mies.']" label="Jm" dense outlined hide-details />
            </v-col>
            <v-col cols="6" sm="4" md="2"><v-text-field v-model="it.priceZl" label="Cena netto (zł)" type="number" min="0" step="0.01" dense outlined hide-details /></v-col>
            <v-col cols="6" sm="2" md="1">
              <v-select v-model.number="it.vatRate" :items="[23, 8, 5, 0]" label="VAT %" dense outlined hide-details />
            </v-col>
            <v-col cols="9" sm="10" md="2" class="text-right text-caption accounting-item-total">
              netto {{ zl(itemNetto(it)) }}<br /><b>brutto {{ zl(itemBrutto(it)) }}</b>
            </v-col>
            <v-col cols="3" sm="2" md="1" class="text-right">
              <v-btn icon small @click="invForm.items.splice(i, 1)" :disabled="invForm.items.length === 1">
                <v-icon small color="red">mdi-close</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <div class="accounting-form-total">
            <div>
              <div>Razem netto: <b>{{ zl(formNetto) }}</b></div>
              <div>Razem VAT: <b>{{ zl(formVat) }}</b></div>
              <div class="text-h6">Do zapłaty: <b>{{ zl(formNetto + formVat) }}</b></div>
            </div>
          </div>
          <v-text-field v-model="invForm.notes" label="Uwagi (na fakturze)" dense outlined hide-details class="mt-2" />
        </v-card-text>
        <v-card-actions class="accounting-dialog-actions">
          <v-spacer />
          <v-btn text @click="invDialog = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="saving" :disabled="!invFormValid" @click="saveInvoice">
            {{ invForm.id ? 'Zapisz zmiany' : 'Wystaw' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ═══ DIALOG: KOSZT ═══ -->
    <v-dialog v-model="costDialog" max-width="700" persistent scrollable content-class="dark-form-dialog">
      <v-card class="accounting-dialog">
        <v-card-title class="accounting-dialog-title"><div class="dialog-title-icon"><v-icon>mdi-receipt-text-outline</v-icon></div><div><strong>{{ costForm.id ? 'Edytuj koszt' : 'Dodaj koszt' }}</strong><span>Faktura otrzymana i sposób odliczenia VAT.</span></div></v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" sm="6"><v-text-field v-model="costForm.number" label="Numer faktury *" dense outlined hide-details /></v-col>
            <v-col cols="12" sm="6"><v-text-field v-model="costForm.issueDate" label="Data wystawienia" type="date" dense outlined hide-details /></v-col>
            <v-col cols="12" sm="8"><v-text-field v-model="costForm.contractorName" label="Kontrahent *" dense outlined hide-details /></v-col>
            <v-col cols="12" sm="4"><v-text-field v-model="costForm.contractorNip" label="NIP" dense outlined hide-details /></v-col>
            <v-col cols="12" sm="6">
              <v-select v-model="costForm.category" :items="categories" label="Kategoria" dense outlined hide-details />
            </v-col>
            <v-col cols="12" sm="6"><v-text-field v-model="costForm.description" label="Opis" dense outlined hide-details /></v-col>
            <v-col cols="12" sm="4"><v-text-field v-model="costForm.nettoZl" label="Netto (zł) *" type="number" step="0.01" dense outlined hide-details hint="Korekta = kwota ujemna" persistent-hint /></v-col>
            <v-col cols="12" sm="4"><v-text-field v-model="costForm.vatZl" label="VAT (zł) *" type="number" step="0.01" dense outlined hide-details /></v-col>
            <v-col cols="12" sm="4">
              <v-text-field :value="((parseFloat(costForm.nettoZl) || 0) + (parseFloat(costForm.vatZl) || 0)).toFixed(2)"
                label="Brutto (zł)" dense outlined hide-details readonly />
            </v-col>
          </v-row>
          <div class="accounting-form-section-title mt-4"><v-icon size="19">mdi-percent-outline</v-icon><span>Odliczenie VAT</span></div>
          <v-radio-group v-model="costForm.deduction" row dense hide-details class="accounting-deduction-options">
            <v-radio label="100% (pełne)" value="D100" />
            <v-radio label="50% (np. auto — użytek mieszany)" value="D50" />
            <v-radio label="25%" value="D25" />
          </v-radio-group>
          <div class="accounting-deduction-summary">
            VAT do odliczenia:
            <b class="green--text text--darken-2">{{ zl(dedPreview) }}</b>
            <span class="grey--text"> z {{ zl(Math.round((parseFloat(costForm.vatZl) || 0) * 100)) }}</span>
          </div>
          <template v-if="costForm.category === 'pensje'">
            <div class="accounting-form-section-title mt-4">
              <v-icon size="19">mdi-account-cash-outline</v-icon><span>Wynagrodzenie</span>
            </div>
            <v-checkbox
              v-model="costForm.withZus"
              label="Dolicz składki pracodawcy (ZUS)"
              hint="Odznaczone = wpisujesz kwotę na rękę i tyle trafia do kosztów"
              persistent-hint
              dense
              class="mt-1"
            />
            <v-row v-if="costForm.withZus" dense class="mt-2">
              <v-col cols="12" sm="6">
                <v-text-field v-model="costForm.bruttoWage" label="Wynagrodzenie brutto (zł)"
                  type="number" step="0.01" dense outlined hide-details />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="costForm.rateAccident" label="Składka wypadkowa (%)"
                  type="number" step="0.01" dense outlined
                  hint="Zależy od firmy — domyślnie 1,67%" persistent-hint />
              </v-col>
            </v-row>
            <div v-if="costForm.withZus" class="accounting-deduction-summary mt-2">
              <div>Emerytalna 9,76% — <b>{{ zl(zusParts.emerytalna) }}</b></div>
              <div>Rentowa 6,50% — <b>{{ zl(zusParts.rentowa) }}</b></div>
              <div>Wypadkowa {{ costForm.rateAccident }}% — <b>{{ zl(zusParts.wypadkowa) }}</b></div>
              <div>Fundusz Pracy 2,45% — <b>{{ zl(zusParts.fp) }}</b></div>
              <div>FGŚP 0,10% — <b>{{ zl(zusParts.fgsp) }}</b></div>
              <div class="mt-1">Składki razem: <b>{{ zl(zusParts.skladki) }}</b></div>
              <div>Całkowity koszt pracodawcy:
                <b class="green--text text--darken-2">{{ zl(zusParts.total) }}</b>
              </div>
            </div>
          </template>

          <v-checkbox v-model="costForm.paid" label="Opłacona" dense hide-details class="mt-2" />
          <v-checkbox
            v-model="costForm.toPay"
            label="Koszt stały firmy (liczy się do kosztów na dashboardzie)"
            hint="Zaznacz np. przy pensji, leasingu, obsłudze księgowej"
            persistent-hint
            dense
            class="mt-2"
          />
        </v-card-text>
        <v-card-actions class="accounting-dialog-actions">
          <v-spacer />
          <v-btn text @click="costDialog = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="saving"
            :disabled="!costForm.number || !costForm.contractorName || !costForm.issueDate"
            @click="saveCost">Zapisz</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ═══ DIALOG: KSeF przychodzące ═══ -->
    <v-dialog v-model="ksefDialog" max-width="900" scrollable content-class="dark-form-dialog">
      <v-card class="accounting-dialog">
        <v-card-title class="accounting-dialog-title"><div class="dialog-title-icon"><v-icon>mdi-bank-transfer-in</v-icon></div><div><strong>Faktury przychodzące z KSeF</strong><span>Wybierz zakres, sprawdź dokumenty i zaimportuj potrzebne koszty.</span></div></v-card-title>
        <v-card-text>
          <div class="accounting-ksef-filters">
            <v-text-field v-model="ksefFrom" label="Od" type="date" dense outlined hide-details />
            <v-text-field v-model="ksefTo" label="Do" type="date" dense outlined hide-details />
            <v-btn color="primary" :loading="ksefLoading" @click="loadKsefIncoming">Pobierz</v-btn>
          </div>
          <div class="accounting-simple-table-wrap" v-if="ksefIncoming.length">
          <v-simple-table dense>
            <thead>
              <tr><th>Nr KSeF</th><th>Numer</th><th>Data</th><th>Kontrahent</th>
                <th class="text-right">Netto</th><th class="text-right">Brutto</th><th></th></tr>
            </thead>
            <tbody>
              <tr v-for="inv in ksefIncoming" :key="inv.ksefNumber">
                <td class="text-caption">{{ inv.ksefNumber }}</td>
                <td>{{ inv.number }}</td>
                <td>{{ fmtDate(inv.issueDate) }}</td>
                <td>{{ inv.contractorName }}</td>
                <td class="text-right">{{ zl(inv.netto) }}</td>
                <td class="text-right">{{ zl(inv.brutto) }}</td>
                <td>
                  <v-chip v-if="inv.imported" x-small color="green" dark>zaimportowana</v-chip>
                  <v-btn v-else x-small outlined color="primary" @click="importKsefCost(inv)">Importuj</v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
          </div>
          <div v-else-if="ksefFetched" class="grey--text">Brak faktur w zadanym okresie.</div>
        </v-card-text>
        <v-card-actions class="accounting-dialog-actions"><v-spacer /><v-btn text @click="ksefDialog = false">Zamknij</v-btn></v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snack.show" :color="snack.color" timeout="5000" bottom>{{ snack.text }}</v-snackbar>
  </div>
</template>

<script>
const axios = require('axios');

const API = () => process.env.VUE_APP_ROOT_API;
const AUTH = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });
const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => new Date(Date.now() + n * 864e5).toISOString().slice(0, 10);

export default {
  name: 'AccountingPage',
  data() {
    const now = new Date();
    return {
      tab: 0,
      fMonth: now.getMonth() + 1,
      fYear: now.getFullYear(),
      ksef: { configured: false, env: '' },
      snack: { show: false, color: 'success', text: '' },
      saving: false,

      invoices: [],
      loadingInv: false,
      invSearch: '',
      invDialog: false,
      invForm: this.blankInvoice(),
      invHeaders: [
        { text: 'Numer', value: 'number' },
        { text: 'Data', value: 'issueDate' },
        { text: 'Nabywca', value: 'buyerName' },
        { text: 'Netto', value: 'nettoTotal', align: 'right' },
        { text: 'VAT', value: 'vatTotal', align: 'right' },
        { text: 'Brutto', value: 'bruttoTotal', align: 'right' },
        { text: 'Status', value: 'status' },
        { text: 'KSeF', value: 'ksefStatus' },
        { text: '', value: 'actions', sortable: false, width: 220 },
      ],

      costs: [],
      loadingCosts: false,
      costSearch: '',
      costCategory: '',
      costDialog: false,
      costForm: this.blankCost(),
      categories: ['paliwo', 'serwis', 'paliwo/serwis', 'leasing', 'ubezpieczenie', 'pensje', 'biuro', 'telefon', 'marketing', 'inne'],
      costHeaders: [
        { text: 'Numer', value: 'number' },
        { text: 'Data', value: 'issueDate' },
        { text: 'Kontrahent', value: 'contractorName' },
        { text: 'Kategoria', value: 'category' },
        { text: 'Netto', value: 'netto', align: 'right' },
        { text: 'VAT', value: 'vat', align: 'right' },
        { text: 'Odlicz.', value: 'deduction' },
        { text: 'VAT do odl.', value: 'vatDeductible', align: 'right' },
        { text: 'Brutto', value: 'brutto', align: 'right' },
        { text: 'Opł.', value: 'paid' },
        { text: 'Weryf.', value: 'verified' },
        { text: '', value: 'actions', sortable: false, width: 150 },
      ],
      // „Do zapłacenia"
      toPay: { costs: [], unpaidCount: 0, unpaidBrutto: 0 },
      loadingToPay: false,
      payees: [],
      payeeDialog: false,
      payeeForm: { contractorNip: '', contractorName: '', accountNo: '' },
      basketDialog: false,
      basketForm: {
        bankFormat: localStorage.getItem('accBasketBank') || 'MBANK',
        senderAccountNo: localStorage.getItem('accBasketAccount') || '',
        senderName: localStorage.getItem('accBasketSender') || '',
      },
      basketSkipped: [],
      toPayHeaders: [
        { text: 'Numer', value: 'number' },
        { text: 'Data', value: 'issueDate' },
        { text: 'Kontrahent', value: 'contractorName' },
        { text: 'Brutto', value: 'brutto', align: 'right' },
        { text: 'Status', value: 'paid' },
        { text: '', value: 'actions', sortable: false, width: 170 },
      ],

      importing: false,
      importDialog: false,
      importFrom: new Date(Date.now() - 7 * 864e5).toISOString().slice(0, 10),
      importTo: new Date().toISOString().slice(0, 10),
      previewDialog: false,
      preview: null,

      summary: null,

      ksefDialog: false,
      ksefIncoming: [],
      ksefLoading: false,
      ksefFetched: false,
      ksefFrom: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10),
      ksefTo: today(),
    };
  },
  computed: {
    // Składki po stronie PRACODAWCY, doliczane do wynagrodzenia brutto.
    // Stawki ustawowe; wypadkowa jest firmowa (0,67–3,33%), więc edytowalna.
    // Uwaga: FP i FGŚP mają zwolnienia (m.in. wiek 55/60+), których tu nie
    // rozpatrujemy — od takich pracowników kwota wyjdzie zawyżona.
    zusParts() {
      const brutto = Math.round((parseFloat(this.costForm.bruttoWage) || 0) * 100);
      const acc = parseFloat(this.costForm.rateAccident);
      const rAcc = isNaN(acc) ? 1.67 : acc;
      const pct = (v) => Math.round((brutto * v) / 100);
      const emerytalna = pct(9.76);
      const rentowa = pct(6.5);
      const wypadkowa = pct(rAcc);
      const fp = pct(2.45);
      const fgsp = pct(0.1);
      const skladki = emerytalna + rentowa + wypadkowa + fp + fgsp;
      return { emerytalna, rentowa, wypadkowa, fp, fgsp, skladki, total: brutto + skladki };
    },
    monthItems() {
      const names = ['styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec', 'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień'];
      // '' = cały rok (backend pomija filtr miesiąca przy pustej wartości)
      return [{ text: 'Cały rok', value: '' }, ...names.map((n, i) => ({ text: n, value: i + 1 }))];
    },
    yearItems() {
      const y = new Date().getFullYear();
      return [y - 2, y - 1, y, y + 1];
    },
    formNetto() {
      return this.invForm.items.reduce((s, it) => s + this.itemNetto(it), 0);
    },
    formVat() {
      return this.invForm.items.reduce((s, it) => s + Math.round((this.itemNetto(it) * (it.vatRate || 0)) / 100), 0);
    },
    invFormValid() {
      return this.invForm.buyerName && this.invForm.issueDate && this.invForm.saleDate &&
        this.invForm.dueDate && this.invForm.items.every((it) => it.name && parseFloat(it.priceZl) >= 0 && it.quantity > 0);
    },
    dedPreview() {
      const vat = Math.round((parseFloat(this.costForm.vatZl) || 0) * 100);
      const pct = { D100: 100, D50: 50, D25: 25 }[this.costForm.deduction] || 100;
      return Math.round((vat * pct) / 100);
    },
    summaryCards() {
      const s = this.summary;
      if (!s) return [];
      return [
        { label: 'Sprzedaż netto', value: this.zl(s.sales.netto), sub: `${s.sales.count} faktur`, color: '' },
        { label: 'VAT należny', value: this.zl(s.sales.vat), sub: 'ze sprzedaży', color: '' },
        { label: 'Koszty netto', value: this.zl(s.costs.netto), sub: `${s.costs.count} dokumentów`, color: '' },
        { label: 'VAT do odliczenia', value: this.zl(s.costs.vatDeductible), sub: `z ${this.zl(s.costs.vat)} VAT`, color: '' },
        { label: 'VAT do zapłaty', value: this.zl(s.vatDue), sub: s.vatDue < 0 ? 'nadwyżka do przeniesienia' : 'należny − naliczony', color: s.vatDue > 0 ? '#101a3a' : 'green', dark: true },
        { label: 'Wynik (netto)', value: this.zl(s.incomeBalance), sub: 'sprzedaż − koszty', color: s.incomeBalance >= 0 ? 'green' : 'red', dark: true },
      ];
    },
  },
  mounted() {
    this.loadKsefInfo();
    this.reloadAll();
  },
  watch: {
    // Gdy liczymy koszt pracodawcy, kwota kosztu = brutto + składki.
    // Pole zostaje edytowalne — nadpisujemy je tylko przy zmianie wyliczenia.
    zusParts(v) {
      if (this.costForm.withZus && this.costForm.category === 'pensje') {
        this.costForm.nettoZl = (v.total / 100).toFixed(2);
      }
    },
  },
  methods: {
    blankInvoice() {
      return {
        id: null, buyerName: '', buyerNip: '', buyerAddress: '', buyerZip: '', buyerCity: '', buyerEmail: '',
        issueDate: today(), saleDate: today(), dueDate: plusDays(14),
        paymentMethod: 'przelew', notes: '',
        items: [this.emptyItem ? this.emptyItem() : { name: '', quantity: 1, unit: 'szt.', priceZl: '', vatRate: 23 }],
      };
    },
    blankCost() {
      return {
        id: null, number: '', contractorName: '', contractorNip: '', issueDate: today(),
        category: 'inne', description: '', nettoZl: '', vatZl: '', deduction: 'D100', paid: false, toPay: false, ksefNumber: null,
        withZus: false, bruttoWage: '', rateAccident: '1.67',
      };
    },
    emptyItem() {
      return { name: '', quantity: 1, unit: 'szt.', priceZl: '', vatRate: 23 };
    },
    zl(g) {
      return ((g || 0) / 100).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' zł';
    },
    fmtDate(d) {
      return d ? new Date(d).toLocaleDateString('pl-PL') : '—';
    },
    itemNetto(it) {
      return Math.round((parseFloat(it.priceZl) || 0) * 100 * (it.quantity || 0));
    },
    itemBrutto(it) {
      const n = this.itemNetto(it);
      return n + Math.round((n * (it.vatRate || 0)) / 100);
    },
    statusColor(s) {
      return { DRAFT: 'grey', ISSUED: 'orange', PAID: 'green', CANCELLED: 'red' }[s] || 'grey';
    },
    statusLabel(s) {
      return { DRAFT: 'szkic', ISSUED: 'wystawiona', PAID: 'opłacona', CANCELLED: 'anulowana' }[s] || s;
    },
    ksefColor(s) {
      return { PENDING: 'orange', SENT: 'green', ERROR: 'red' }[s] || 'grey';
    },
    ksefLabel(s) {
      return { PENDING: 'wysyłanie…', SENT: 'w KSeF', ERROR: 'błąd' }[s] || s;
    },
    dedColor(d) {
      return { D100: 'green', D50: 'orange', D25: 'blue-grey' }[d] || 'grey';
    },
    dedLabel(d) {
      return { D100: '100%', D50: '50%', D25: '25%' }[d] || d;
    },
    toast(text, color = 'success') {
      this.snack = { show: true, color, text };
    },
    errMsg(e) {
      return e?.response?.data?.message?.toString?.() || e.message || 'Błąd';
    },

    reloadAll() {
      this.loadInvoices();
      this.loadCosts();
      this.loadSummary();
      this.loadToPay();
      this.loadPayees();
    },

    // ── „Do zapłacenia" ──
    async loadToPay() {
      this.loadingToPay = true;
      try {
        const res = await axios.get(API() + 'accounting/costs/to-pay', { headers: AUTH() });
        this.toPay = res.data.data;
      } catch (e) { console.log(e); } finally { this.loadingToPay = false; }
    },
    async loadPayees() {
      try {
        const res = await axios.get(API() + 'accounting/payees', { headers: AUTH() });
        this.payees = res.data.data;
      } catch (e) { console.log(e); }
    },
    async toggleToPay(item, toPay) {
      try {
        await axios.patch(API() + 'accounting/costs/' + item.id + '/to-pay', { toPay }, { headers: AUTH() });
        item.toPay = toPay;
        this.toast(toPay ? 'Dodano do „Do zapłacenia"' : 'Zdjęto z listy do zapłaty');
        this.loadToPay();
      } catch (e) { this.toast(this.errMsg(e), 'error'); }
    },
    async markCostPaid(item, paid) {
      try {
        await axios.patch(API() + 'accounting/costs/' + item.id + '/paid', { paid }, { headers: AUTH() });
        this.toast(paid ? 'Oznaczono jako opłaconą' : 'Cofnięto opłacenie');
        this.loadToPay();
        this.loadCosts();
      } catch (e) { this.toast(this.errMsg(e), 'error'); }
    },
    openPayeeDialog(payee, fromCost) {
      this.payeeForm = payee
        ? { contractorNip: payee.contractorNip, contractorName: payee.contractorName, accountNo: payee.accountNo || '' }
        : fromCost
          ? { contractorNip: fromCost.contractorNip || '', contractorName: fromCost.contractorName || '', accountNo: '' }
          : { contractorNip: '', contractorName: '', accountNo: '' };
      this.payeeDialog = true;
    },
    async savePayee() {
      this.saving = true;
      try {
        const res = await axios.post(API() + 'accounting/payees', this.payeeForm, { headers: AUTH() });
        const n = res.data?.data?.flaggedExisting || 0;
        this.toast('Adresat zapisany' + (n ? ` — oznaczono ${n} zaległych faktur` : ''));
        this.payeeDialog = false;
        this.loadPayees();
        this.loadToPay();
        this.loadCosts();
      } catch (e) { this.toast(this.errMsg(e), 'error'); } finally { this.saving = false; }
    },
    async removePayee(p) {
      try {
        await axios.delete(API() + 'accounting/payees/' + p.id, { headers: AUTH() });
        this.toast('Adresat usunięty — nowe faktury nie będą już auto-oznaczane');
        this.loadPayees();
      } catch (e) { this.toast(this.errMsg(e), 'error'); }
    },
    async generateBasket() {
      this.saving = true;
      this.basketSkipped = [];
      try {
        const res = await axios.post(API() + 'accounting/costs/to-pay/transfer-file', this.basketForm, { headers: AUTH() });
        const d = res.data.data;
        this.basketSkipped = d.skipped || [];
        localStorage.setItem('accBasketBank', this.basketForm.bankFormat);
        localStorage.setItem('accBasketAccount', this.basketForm.senderAccountNo);
        localStorage.setItem('accBasketSender', this.basketForm.senderName);
        const blob = new Blob([d.file], { type: 'text/plain;charset=utf-8' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = d.fileName;
        a.click();
        URL.revokeObjectURL(a.href);
        this.toast(`Koszyk: ${d.count} przelewów na ${this.zl(d.totalBrutto)}` + (d.skipped?.length ? ` (pominięte: ${d.skipped.length})` : ''));
        if (!d.skipped?.length) this.basketDialog = false;
      } catch (e) { this.toast(this.errMsg(e), 'error'); } finally { this.saving = false; }
    },
    async loadKsefInfo() {
      try {
        const res = await axios.get(API() + 'accounting/ksef/info', { headers: AUTH() });
        this.ksef = res.data.data;
      } catch (e) { console.log(e); }
    },
    async loadInvoices() {
      this.loadingInv = true;
      try {
        const res = await axios.get(API() + 'accounting/invoices', {
          headers: AUTH(),
          params: { month: this.fMonth, year: this.fYear, search: this.invSearch || undefined },
        });
        this.invoices = res.data.data;
      } catch (e) { this.toast(this.errMsg(e), 'error'); }
      this.loadingInv = false;
    },
    async loadCosts() {
      this.loadingCosts = true;
      try {
        const res = await axios.get(API() + 'accounting/costs', {
          headers: AUTH(),
          params: { month: this.fMonth, year: this.fYear, search: this.costSearch || undefined, category: this.costCategory || undefined },
        });
        this.costs = res.data.data;
      } catch (e) { this.toast(this.errMsg(e), 'error'); }
      this.loadingCosts = false;
    },
    async loadSummary() {
      try {
        const res = await axios.get(API() + 'accounting/summary', {
          headers: AUTH(), params: { month: this.fMonth, year: this.fYear },
        });
        this.summary = res.data.data;
      } catch (e) { console.log(e); }
    },

    openInvoiceDialog(item) {
      if (item) {
        this.invForm = {
          id: item.id,
          buyerName: item.buyerName, buyerNip: item.buyerNip, buyerAddress: item.buyerAddress,
          buyerZip: item.buyerZip, buyerCity: item.buyerCity, buyerEmail: item.buyerEmail,
          issueDate: item.issueDate.slice(0, 10), saleDate: item.saleDate.slice(0, 10), dueDate: item.dueDate.slice(0, 10),
          paymentMethod: item.paymentMethod, notes: item.notes,
          items: item.items.map((it) => ({
            name: it.name, quantity: it.quantity, unit: it.unit,
            priceZl: (it.nettoUnit / 100).toFixed(2), vatRate: it.vatRate,
          })),
        };
      } else {
        this.invForm = this.blankInvoice();
      }
      this.invDialog = true;
    },
    async saveInvoice() {
      this.saving = true;
      const payload = {
        buyerName: this.invForm.buyerName, buyerNip: this.invForm.buyerNip || undefined,
        buyerAddress: this.invForm.buyerAddress || undefined, buyerZip: this.invForm.buyerZip || undefined,
        buyerCity: this.invForm.buyerCity || undefined, buyerEmail: this.invForm.buyerEmail || undefined,
        issueDate: this.invForm.issueDate, saleDate: this.invForm.saleDate, dueDate: this.invForm.dueDate,
        paymentMethod: this.invForm.paymentMethod, notes: this.invForm.notes || undefined,
        items: this.invForm.items.map((it) => ({
          name: it.name, quantity: parseFloat(it.quantity) || 1, unit: it.unit,
          nettoUnit: Math.round((parseFloat(it.priceZl) || 0) * 100), vatRate: it.vatRate,
        })),
      };
      try {
        if (this.invForm.id) {
          await axios.patch(API() + 'accounting/invoices/' + this.invForm.id, payload, { headers: AUTH() });
          this.toast('Faktura zaktualizowana');
        } else {
          const res = await axios.post(API() + 'accounting/invoices', payload, { headers: AUTH() });
          this.toast('Wystawiono fakturę ' + res.data.data.number);
        }
        this.invDialog = false;
        this.loadInvoices();
        this.loadSummary();
      } catch (e) { this.toast(this.errMsg(e), 'error'); }
      this.saving = false;
    },
    async deleteInvoice(item) {
      if (!confirm(`Usunąć fakturę ${item.number}?`)) return;
      try {
        await axios.delete(API() + 'accounting/invoices/' + item.id, { headers: AUTH() });
        this.toast('Usunięto');
        this.loadInvoices();
        this.loadSummary();
      } catch (e) { this.toast(this.errMsg(e), 'error'); }
    },
    async togglePaid(item) {
      try {
        await axios.patch(API() + 'accounting/invoices/' + item.id + '/paid',
          { paid: item.status !== 'PAID' }, { headers: AUTH() });
        this.loadInvoices();
        this.loadSummary();
      } catch (e) { this.toast(this.errMsg(e), 'error'); }
    },
    async downloadPdf(item) {
      try {
        const res = await axios.get(API() + 'accounting/invoices/' + item.id + '/pdf', {
          headers: AUTH(), responseType: 'blob',
        });
        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = item.number.replace(/\//g, '_') + '.pdf';
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) { this.toast(this.errMsg(e), 'error'); }
    },
    async sendKsef(item) {
      if (!this.ksef.configured) {
        this.toast('KSeF nie jest skonfigurowany (KSEF_NIP, KSEF_TOKEN, KSEF_PUBLIC_KEY w backendzie)', 'error');
        return;
      }
      if (!confirm(`Wysłać fakturę ${item.number} do KSeF (${this.ksef.env})?`)) return;
      try {
        await axios.post(API() + 'accounting/invoices/' + item.id + '/ksef', {}, { headers: AUTH() });
        this.toast('Wysłano do KSeF');
      } catch (e) { this.toast(this.errMsg(e), 'error'); }
      this.loadInvoices();
    },
    async refreshKsef(item) {
      try {
        await axios.post(API() + 'accounting/invoices/' + item.id + '/ksef-status', {}, { headers: AUTH() });
        this.loadInvoices();
      } catch (e) { this.toast(this.errMsg(e), 'error'); }
    },

    openCostDialog(item) {
      if (item) {
        this.costForm = {
          id: item.id, number: item.number, contractorName: item.contractorName, contractorNip: item.contractorNip,
          issueDate: item.issueDate.slice(0, 10), category: item.category, description: item.description,
          nettoZl: (item.netto / 100).toFixed(2), vatZl: (item.vat / 100).toFixed(2),
          deduction: item.deduction, paid: item.paid, toPay: item.toPay, ksefNumber: item.ksefNumber,
        };
      } else {
        this.costForm = this.blankCost();
      }
      this.costDialog = true;
    },
    async saveCost() {
      this.saving = true;
      const netto = Math.round((parseFloat(this.costForm.nettoZl) || 0) * 100);
      const vat = Math.round((parseFloat(this.costForm.vatZl) || 0) * 100);
      const payload = {
        number: this.costForm.number, contractorName: this.costForm.contractorName,
        contractorNip: this.costForm.contractorNip || undefined,
        issueDate: this.costForm.issueDate, category: this.costForm.category,
        description: this.costForm.description || undefined,
        netto, vat, brutto: netto + vat,
        deduction: this.costForm.deduction, paid: !!this.costForm.paid,
        toPay: !!this.costForm.toPay,
        ksefNumber: this.costForm.ksefNumber || undefined,
      };
      try {
        if (this.costForm.id) {
          await axios.patch(API() + 'accounting/costs/' + this.costForm.id, payload, { headers: AUTH() });
        } else {
          await axios.post(API() + 'accounting/costs', payload, { headers: AUTH() });
        }
        this.toast('Zapisano koszt');
        this.costDialog = false;
        this.loadCosts();
        this.loadSummary();
      } catch (e) { this.toast(this.errMsg(e), 'error'); }
      this.saving = false;
    },
    async deleteCost(item) {
      if (!confirm(`Usunąć koszt ${item.number}?`)) return;
      try {
        await axios.delete(API() + 'accounting/costs/' + item.id, { headers: AUTH() });
        this.toast('Usunięto');
        this.loadCosts();
        this.loadSummary();
      } catch (e) { this.toast(this.errMsg(e), 'error'); }
    },

    openKsefIncoming() {
      if (!this.ksef.configured) {
        this.toast('KSeF nie jest skonfigurowany (KSEF_NIP, KSEF_TOKEN, KSEF_PUBLIC_KEY w backendzie)', 'error');
        return;
      }
      this.ksefDialog = true;
    },
    async loadKsefIncoming() {
      this.ksefLoading = true;
      try {
        const res = await axios.get(API() + 'accounting/ksef/incoming', {
          headers: AUTH(), params: { from: this.ksefFrom, to: this.ksefTo },
        });
        this.ksefIncoming = res.data.data;
        this.ksefFetched = true;
      } catch (e) { this.toast(this.errMsg(e), 'error'); }
      this.ksefLoading = false;
    },
    async openCostPreview(item) {
      this.preview = null;
      this.previewDialog = true;
      try {
        const res = await axios.get(API() + 'accounting/costs/' + item.id + '/details', { headers: AUTH() });
        this.preview = res.data.data;
        // backfill XML-a mógł uzupełnić opis — odśwież listę w tle
        if (!item.hasXml && this.preview.hasXml) this.loadCosts();
      } catch (e) {
        this.previewDialog = false;
        this.toast(this.errMsg(e), 'error');
      }
    },
    debouncedInv() {
      clearTimeout(this.__ti);
      this.__ti = setTimeout(() => this.loadInvoices(), 400);
    },
    debouncedCosts() {
      clearTimeout(this.__tc);
      this.__tc = setTimeout(() => this.loadCosts(), 400);
    },
    openImportDialog() {
      this.importDialog = true;
    },
    setImportRange(days) {
      this.importFrom = new Date(Date.now() - days * 864e5).toISOString().slice(0, 10);
      this.importTo = new Date().toISOString().slice(0, 10);
    },
    async importNow() {
      this.importing = true;
      try {
        const res = await axios.post(API() + 'accounting/ksef/import-now',
          { from: this.importFrom, to: this.importTo }, { headers: AUTH() });
        const d = res.data.data;
        const s = d.sales || { checked: 0, imported: 0, errors: [] };
        const errs = (d.errors.length + s.errors.length) ? `, błędy: ${d.errors.length + s.errors.length}` : '';
        this.toast(`Koszty: +${d.imported} (z ${d.checked}) · Sprzedaż: +${s.imported} (z ${s.checked})${errs}`);
        this.importDialog = false;
        this.loadCosts();
        this.loadInvoices();
        this.loadSummary();
      } catch (e) { this.toast(this.errMsg(e), 'error'); }
      this.importing = false;
    },
    async downloadZip() {
      try {
        const res = await axios.get(API() + 'accounting/costs/zip', {
          headers: AUTH(), responseType: 'blob',
          params: { year: this.fYear, month: this.fMonth },
        });
        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `faktury-ksef-${this.fYear}-${String(this.fMonth).padStart(2, '0')}.zip`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        // blob error → tekst
        if (e?.response?.data instanceof Blob) {
          const t = await e.response.data.text();
          try { this.toast(JSON.parse(t).message || t, 'error'); } catch { this.toast(t, 'error'); }
        } else this.toast(this.errMsg(e), 'error');
      }
    },
    async clearXmls() {
      if (!confirm(`Usunąć z serwera XML-e faktur za ${this.fMonth}/${this.fYear}?\n\nKoszty i kwoty ZOSTANĄ — znikną tylko pliki źródłowe (wizualizacja i ZIP przestaną działać dla tego miesiąca). Najpierw pobierz ZIP!`)) return;
      try {
        const res = await axios.post(API() + 'accounting/costs/clear-xml',
          { year: this.fYear, month: this.fMonth }, { headers: AUTH() });
        this.toast(`Usunięto XML-e: ${res.data.data.cleared}`);
        this.loadCosts();
      } catch (e) { this.toast(this.errMsg(e), 'error'); }
    },
    async downloadCostPdf(item) {
      try {
        const res = await axios.get(API() + 'accounting/costs/' + item.id + '/pdf', {
          headers: AUTH(), responseType: 'blob',
        });
        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = item.number.replace(/[^\w.-]+/g, '_') + '_wizualizacja.pdf';
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) { this.toast(this.errMsg(e), 'error'); }
    },
    async downloadCostXml(item) {
      try {
        const res = await axios.get(API() + 'accounting/costs/' + item.id + '/xml', {
          headers: AUTH(), responseType: 'blob',
        });
        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/xml' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = item.number.replace(/[^\w.-]+/g, '_') + '.xml';
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) { this.toast(this.errMsg(e), 'error'); }
    },
    importKsefCost(inv) {
      this.costForm = {
        id: null, number: inv.number || '', contractorName: inv.contractorName || '',
        contractorNip: inv.contractorNip || '', issueDate: (inv.issueDate || today()).slice(0, 10),
        category: 'inne', description: 'Import z KSeF',
        nettoZl: (inv.netto / 100).toFixed(2), vatZl: (inv.vat / 100).toFixed(2),
        deduction: 'D100', paid: false, ksefNumber: inv.ksefNumber,
      };
      this.ksefDialog = false;
      this.costDialog = true;
    },
  },
};
</script>

<style scoped>
.accounting-page {
  min-height: calc(100vh - 56px);
  margin: -24px;
  padding: 20px 20px 48px;
  overflow: hidden;
  background:
    radial-gradient(circle at 88% -12%, rgba(84, 120, 255, 0.13), transparent 31%),
    #181C28;
  color: #F5F7FC;
}

.accounting-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 22px;
  margin-bottom: 14px;
}
.accounting-heading { display: flex; align-items: center; gap: 13px; min-width: 0; }
.accounting-heading-icon {
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(126, 151, 255, 0.24);
  border-radius: 15px;
  background: linear-gradient(135deg, #2962FF, #5478FF);
  box-shadow: 0 12px 26px -14px rgba(41, 98, 255, 0.9);
}
.accounting-eyebrow,
.accounting-section-eyebrow {
  display: block;
  margin-bottom: 2px;
  color: #7F8AA2;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.11em;
  text-transform: uppercase;
}
.accounting-heading h1 {
  margin: 0;
  color: #FFF;
  font-size: 24px;
  font-weight: 850;
  letter-spacing: -0.035em;
  line-height: 1.1;
}
.accounting-heading p {
  margin: 5px 0 0;
  color: #9DA9BE;
  font-size: 12.5px;
  line-height: 1.4;
}

.accounting-period-card {
  min-width: 510px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 17px;
  background: rgba(35, 40, 56, 0.94);
  box-shadow: 0 20px 45px -34px rgba(0, 0, 0, 0.9);
}
.accounting-period-copy { min-width: 96px; }
.accounting-period-copy span,
.accounting-ksef-state small {
  display: block;
  color: #737E96;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.accounting-period-copy strong {
  display: block;
  margin-top: 2px;
  color: #FFF;
  font-size: 16px;
  font-variant-numeric: tabular-nums;
}
.accounting-period-fields { display: flex; gap: 8px; }
.accounting-month-select { width: 138px; }
.accounting-year-select { width: 100px; }
.accounting-ksef-state {
  min-width: 130px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 12px;
  border-left: 1px solid rgba(255, 255, 255, 0.07);
}
.accounting-ksef-state strong { display: block; margin-top: 2px; color: #DDE3EE; font-size: 12.5px; white-space: nowrap; }
.accounting-status-dot { width: 8px; height: 8px; flex: 0 0 8px; border-radius: 50%; background: #778096; }
.accounting-ksef-state.is-connected .accounting-status-dot { background: #5DE494; box-shadow: 0 0 0 4px rgba(93, 228, 148, 0.1); }
.accounting-ksef-state.is-connected strong { color: #8DECB4; }

.accounting-overview {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}
.accounting-overview-card {
  position: relative;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 14px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.065);
  border-radius: 16px;
  background: linear-gradient(145deg, rgba(38, 44, 61, 0.96), rgba(31, 36, 52, 0.96));
}
.accounting-overview-card::after {
  position: absolute;
  right: -24px;
  bottom: -34px;
  width: 90px;
  height: 90px;
  border-radius: 50%;
  background: rgba(84, 120, 255, 0.07);
  content: "";
}
.overview-icon {
  width: 37px;
  height: 37px;
  flex: 0 0 37px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: rgba(84, 120, 255, 0.13);
}
.overview-icon >>> .v-icon { color: #7D99FF; }
.overview-copy { min-width: 0; display: flex; flex-direction: column; position: relative; z-index: 1; }
.overview-copy span { color: #929DB2; font-size: 12px; font-weight: 700; }
.overview-copy strong {
  overflow: hidden;
  margin-top: 1px;
  color: #FFF;
  font-size: clamp(15px, 1.5vw, 19px);
  font-weight: 850;
  letter-spacing: -0.025em;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.overview-copy small { margin-top: 1px; color: #68738B; font-size: 11.5px; }
.overview-costs .overview-icon { background: rgba(255, 183, 77, 0.11); }
.overview-costs .overview-icon >>> .v-icon { color: #FFCA7A; }
.overview-vat .overview-icon { background: rgba(255, 119, 116, 0.11); }
.overview-vat .overview-icon >>> .v-icon { color: #FF9693; }
.overview-vat.is-credit .overview-icon,
.overview-result.is-positive .overview-icon { background: rgba(88, 216, 141, 0.11); }
.overview-vat.is-credit .overview-icon >>> .v-icon,
.overview-result.is-positive .overview-icon >>> .v-icon { color: #69E49C; }
.overview-result.is-negative .overview-icon { background: rgba(255, 119, 116, 0.11); }
.overview-result.is-negative .overview-icon >>> .v-icon { color: #FF9693; }

.accounting-tabs-shell {
  margin-bottom: 10px;
  padding: 5px;
  border: 1px solid rgba(255, 255, 255, 0.065);
  border-radius: 15px;
  background: #202536;
}
.accounting-tabs-shell >>> .v-tabs-bar { height: 43px; }
.accounting-tabs-shell >>> .v-tab {
  min-width: 0;
  padding: 0 18px;
  border-radius: 11px;
  color: #8994A9;
  font-size: 13px;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}
.accounting-tabs-shell >>> .v-tab::before { border-radius: 11px; }
.accounting-tabs-shell >>> .v-tab--active { background: rgba(84, 120, 255, 0.16); color: #FFF; }
.accounting-tabs-shell >>> .v-tab--active .v-icon { color: #7D99FF; }
.accounting-tabs-shell >>> .v-tabs-slider-wrapper { display: none; }
.tab-count {
  min-width: 20px;
  margin-left: 7px;
  padding: 2px 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.075);
  color: #BAC4D6;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}

.tab-count--alert {
  background: rgba(255, 152, 0, 0.22);
  color: #FFB74D;
}

.accounting-payees {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}
.accounting-payees-label {
  font-size: 13px;
  color: #8b96ab;
  margin-right: 4px;
}

.accounting-content-panel {
  padding: 18px;
  border: 1px solid rgba(255, 255, 255, 0.065);
  border-radius: 18px;
  background: #232838;
  box-shadow: 0 22px 45px -38px rgba(0, 0, 0, 0.9);
}
.accounting-section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 14px;
}
.accounting-section-head h2 { margin: 0; color: #FFF; font-size: 18px; font-weight: 820; letter-spacing: -0.025em; }
.accounting-section-head p { max-width: 680px; margin: 4px 0 0; color: #8E99AE; font-size: 13px; line-height: 1.4; }
.accounting-primary-btn { min-height: 40px !important; border-radius: 11px !important; box-shadow: 0 12px 24px -16px rgba(41, 98, 255, 0.85) !important; text-transform: none !important; }
.accounting-section-actions { display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 8px; }
.accounting-section-actions >>> .v-btn { min-height: 40px; margin: 0; border-radius: 11px; font-size: 12.5px; font-weight: 700; letter-spacing: 0; text-transform: none; }
.accounting-automation-note { display: inline-flex; align-items: center; gap: 5px; margin-top: 8px; color: #75DEA0; font-size: 12px; }
.accounting-automation-note >>> .v-icon { color: #5DD98F; }

.accounting-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  padding: 10px;
  border: 1px solid rgba(255, 255, 255, 0.055);
  border-radius: 14px;
  background: #1D2231;
}
.accounting-search { max-width: 420px; }
.accounting-category-select { max-width: 205px; }
.accounting-result-count { display: flex; align-items: center; gap: 6px; margin-left: auto; padding: 0 7px; color: #7F8AA1; font-size: 12px; white-space: nowrap; }
.accounting-result-count b { color: #DDE3EE; font-variant-numeric: tabular-nums; }
.accounting-result-count >>> .v-icon { color: #68758F; }

.accounting-table-shell {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 15px;
}
.accounting-page >>> .v-data-table { background: transparent !important; color: #E8ECF4 !important; }
.accounting-page >>> .v-data-table__wrapper { overflow-x: auto; }
.accounting-page >>> .v-data-table thead tr th {
  height: 42px;
  background: #1D2231 !important;
  color: #748097 !important;
  font-size: 11.5px !important;
  font-weight: 800 !important;
  letter-spacing: 0.055em;
  text-transform: uppercase;
  white-space: nowrap;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06) !important;
}
.accounting-page >>> .v-data-table tbody tr { background: #232838 !important; transition: background 0.15s ease; }
.accounting-page >>> .v-data-table tbody tr:hover { background: #292F42 !important; }
.accounting-page >>> .v-data-table tbody tr td {
  min-height: 54px;
  color: #DCE1EB !important;
  font-size: 12.5px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04) !important;
}
.accounting-page >>> .v-data-footer {
  min-height: 48px;
  background: #1D2231 !important;
  color: #8E99AE !important;
  border-top: 1px solid rgba(255, 255, 255, 0.055) !important;
}
.accounting-page >>> .v-data-footer .v-btn .v-icon,
.accounting-page >>> .v-data-footer__select .v-select__selection { color: #B3BDCF !important; }
.accounting-primary-cell { display: flex; align-items: center; gap: 6px; min-width: 112px; }
.accounting-primary-cell strong,
.accounting-document-number { color: #F5F7FB; font-weight: 750; white-space: nowrap; font-variant-numeric: tabular-nums; }
.accounting-party-cell { min-width: 125px; display: flex; flex-direction: column; }
.accounting-party-cell strong { max-width: 210px; overflow: hidden; color: #EFF2F7; text-overflow: ellipsis; white-space: nowrap; }
.accounting-party-cell small { margin-top: 2px; color: #6F7B92; font-size: 11.5px; }
.accounting-money { display: inline-block; color: #CAD1DE; white-space: nowrap; font-variant-numeric: tabular-nums; }
.accounting-money--muted { color: #8D98AD; }
.accounting-money--strong { color: #FFF; font-weight: 800; }
.accounting-money--positive,
.is-positive-text { color: #69E49C !important; }
.accounting-status-chip { height: 23px !important; font-size: 11px !important; font-weight: 750 !important; }
.accounting-category-pill {
  display: inline-flex;
  padding: 4px 8px;
  border: 1px solid rgba(139, 159, 235, 0.12);
  border-radius: 999px;
  background: rgba(84, 120, 255, 0.08);
  color: #B6C3EF;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.accounting-inline-status { display: inline-flex; align-items: center; gap: 4px; font-size: 11.5px; font-weight: 700; white-space: nowrap; }
.accounting-inline-status >>> .v-icon { color: inherit !important; }
.accounting-inline-status.is-success { color: #69E49C; }
.accounting-inline-status.is-muted { color: #7D879B; }
.accounting-row-actions { display: flex; align-items: center; gap: 3px; }
.accounting-row-btn {
  width: 30px !important;
  height: 30px !important;
  color: #8D98AC !important;
  border: 1px solid transparent;
  border-radius: 9px !important;
}
.accounting-row-btn:hover { color: #FFF !important; border-color: rgba(255, 255, 255, 0.08); background: rgba(255, 255, 255, 0.045); }
.accounting-row-btn.is-primary { color: #83A0FF !important; background: rgba(84, 120, 255, 0.09); }
.accounting-row-btn.is-danger:hover { color: #FF8E8B !important; background: rgba(255, 119, 116, 0.08); }

.accounting-mobile-list { display: none; }
.accounting-mobile-card {
  padding: 14px;
  border: 1px solid rgba(255, 255, 255, 0.065);
  border-radius: 15px;
  background: #1E2332;
}
.mobile-card-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
.mobile-card-top > div { min-width: 0; display: flex; flex-direction: column; }
.mobile-card-top small { color: #737F96; font-size: 11px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; }
.mobile-card-top strong { overflow: hidden; color: #FFF; font-size: 14px; text-overflow: ellipsis; white-space: nowrap; }
.mobile-card-party { display: flex; flex-direction: column; margin-top: 10px; color: #DDE2EC; font-size: 13px; font-weight: 650; }
.mobile-card-party small { margin-top: 2px; color: #707C92; font-size: 11.5px; font-weight: 500; }
.mobile-card-facts { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 6px; margin-top: 11px; }
.mobile-card-facts > div { min-width: 0; padding: 9px; border-radius: 10px; background: rgba(255, 255, 255, 0.027); }
.mobile-card-facts span { display: block; color: #717D94; font-size: 10.5px; }
.mobile-card-facts strong { display: block; overflow: hidden; margin-top: 2px; color: #F3F5F9; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; font-variant-numeric: tabular-nums; }
.mobile-card-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 2px; margin: 10px -6px -5px; padding-top: 8px; border-top: 1px solid rgba(255, 255, 255, 0.05); }
.mobile-card-actions >>> .v-btn { min-width: 0 !important; padding: 0 7px !important; color: #A9B4C6; font-size: 11.5px; letter-spacing: 0; text-transform: none; }
.mobile-card-actions .is-danger-text { color: #FF9794 !important; }
.mobile-verification-note { display: flex; align-items: center; gap: 5px; margin-top: 9px; padding: 7px 9px; border-radius: 9px; background: rgba(255, 183, 77, 0.09); color: #FFD18C; font-size: 11.5px; }
.mobile-verification-note >>> .v-icon { color: #FFBE63; }
.accounting-empty-state { min-height: 180px; display: flex; flex-direction: column; align-items: center; justify-content: center; color: #667289; text-align: center; }
.accounting-empty-state >>> .v-icon { margin-bottom: 8px; color: #536078; }
.accounting-empty-state strong { color: #AAB4C5; font-size: 14px; }
.accounting-empty-state span { margin-top: 3px; font-size: 12px; }

.accounting-summary-grid { margin-top: -4px; }
.accounting-summary-card {
  position: relative;
  height: 100%;
  min-height: 118px;
  padding: 14px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  border-radius: 15px !important;
  background: #1E2332 !important;
  color: #FFF !important;
  box-shadow: none !important;
}
.accounting-summary-card::after { position: absolute; top: 12px; bottom: 12px; left: 0; width: 2px; border-radius: 0 3px 3px 0; background: #5478FF; content: ""; }
.accounting-summary-card.is-key { background: linear-gradient(145deg, #202A47, #1E2332) !important; }
.accounting-summary-card.is-positive::after { background: #55D68B; }
.accounting-summary-card.is-negative::after { background: #FF7774; }
.accounting-summary-icon { width: 31px; height: 31px; display: flex; align-items: center; justify-content: center; margin-bottom: 9px; border-radius: 10px; background: rgba(84, 120, 255, 0.1); }
.accounting-summary-icon >>> .v-icon { color: #7896FF; }
.accounting-summary-label { color: #8995AA; font-size: 11.5px; font-weight: 700; }
.accounting-summary-value { margin-top: 2px; color: #FFF; font-size: 16px; font-weight: 850; letter-spacing: -0.025em; white-space: nowrap; font-variant-numeric: tabular-nums; }
.accounting-summary-sub { margin-top: 2px; color: #647087; font-size: 11px; }
.accounting-breakdown-grid { margin-top: 8px; }
.accounting-breakdown-card { height: 100%; overflow: hidden; border: 1px solid rgba(255, 255, 255, 0.06) !important; border-radius: 15px !important; background: #1E2332 !important; color: #FFF !important; box-shadow: none !important; }
.accounting-breakdown-head { min-height: 66px; display: flex; align-items: center; gap: 9px; padding: 13px 14px 10px; }
.accounting-breakdown-head .breakdown-icon { width: 32px; height: 32px; flex: 0 0 32px; display: flex; align-items: center; justify-content: center; border-radius: 10px; background: rgba(84, 120, 255, 0.1); }
.accounting-breakdown-head .breakdown-icon >>> .v-icon { color: #7896FF; }
.accounting-breakdown-head strong { display: block; color: #EEF1F7; font-size: 13px; }
.accounting-breakdown-head span { display: block; margin-top: 2px; color: #6C7890; font-size: 11px; }
.accounting-page >>> .accounting-breakdown-card .v-data-table,
.accounting-page >>> .accounting-breakdown-card .v-data-table tbody tr { background: transparent !important; }
.accounting-page >>> .accounting-breakdown-card .v-data-table thead tr th { height: 35px; background: rgba(255, 255, 255, 0.025) !important; }
.accounting-page >>> .accounting-breakdown-card .v-data-table tbody tr td { height: 38px; font-size: 12px !important; }

.accounting-page >>> .v-text-field--outlined fieldset,
.accounting-page >>> .v-select--outlined fieldset,
.accounting-dialog >>> .v-text-field--outlined fieldset,
.accounting-dialog >>> .v-select--outlined fieldset { border-color: rgba(255, 255, 255, 0.1) !important; }
.accounting-page >>> .v-text-field--outlined .v-input__slot,
.accounting-page >>> .v-select--outlined .v-input__slot,
.accounting-dialog >>> .v-text-field--outlined .v-input__slot,
.accounting-dialog >>> .v-select--outlined .v-input__slot {
  min-height: 40px !important;
  border-radius: 11px !important;
  background: #181C28 !important;
}
.accounting-page >>> .v-label,
.accounting-dialog >>> .v-label { color: #6F7A91 !important; font-size: 13px !important; }
.accounting-page >>> .v-text-field input,
.accounting-page >>> .v-select__selection,
.accounting-dialog >>> .v-text-field input,
.accounting-dialog >>> .v-select__selection { color: #F2F4F8 !important; font-size: 13px; }
.accounting-page >>> .v-input__prepend-inner .v-icon,
.accounting-dialog >>> .v-input__prepend-inner .v-icon { color: #66738C !important; }

.accounting-dialog { background: #202536 !important; color: #EFF2F7 !important; }
.accounting-dialog-title { display: flex; align-items: center; gap: 10px; padding: 17px 20px !important; border-bottom: 1px solid rgba(255, 255, 255, 0.06); }
.dialog-title-icon { width: 38px; height: 38px; flex: 0 0 38px; display: flex; align-items: center; justify-content: center; border-radius: 12px; background: rgba(84, 120, 255, 0.13); }
.dialog-title-icon >>> .v-icon { color: #7896FF; }
.accounting-dialog-title strong { display: block; color: #FFF; font-size: 16px; line-height: 1.15; }
.accounting-dialog-title span { display: block; margin-top: 3px; color: #7E8A9F; font-size: 12px; white-space: normal; }
.accounting-dialog >>> .v-card__text { padding: 18px 20px !important; color: #D9DEE8 !important; }
.accounting-dialog-actions { position: sticky; bottom: 0; z-index: 2; padding: 10px 16px !important; border-top: 1px solid rgba(255, 255, 255, 0.06); background: #202536; }
.accounting-dialog-actions >>> .v-btn { border-radius: 10px; letter-spacing: 0; text-transform: none; }
.accounting-form-section-title { display: flex; align-items: center; gap: 6px; margin-bottom: 8px; color: #C9D0DD; font-size: 12.5px; font-weight: 800; }
.accounting-form-section-title >>> .v-icon { color: #7896FF; }
.accounting-form-section-title--actions { justify-content: space-between; }
.accounting-form-section-title--actions > div { display: flex; align-items: center; gap: 6px; }
.accounting-invoice-item { margin: 0 0 7px !important; padding: 7px 4px; border: 1px solid rgba(255, 255, 255, 0.05); border-radius: 12px; background: rgba(255, 255, 255, 0.018); }
.accounting-item-total { color: #8E99AD; font-variant-numeric: tabular-nums; }
.accounting-item-total b { color: #F2F4F8; }
.accounting-form-total,
.accounting-preview-total { display: flex; justify-content: flex-end; margin-top: 12px; padding: 12px 14px; border: 1px solid rgba(84, 120, 255, 0.14); border-radius: 12px; background: rgba(84, 120, 255, 0.075); color: #AEB8C9; text-align: right; font-variant-numeric: tabular-nums; }
.accounting-form-total b,
.accounting-preview-total b { color: #FFF; }
.accounting-deduction-options { margin-top: 0; }
.accounting-deduction-options >>> .v-input--radio-group__input { gap: 8px; }
.accounting-deduction-options >>> .v-radio { min-height: 38px; margin: 0 !important; padding: 8px 10px; border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 10px; background: rgba(255, 255, 255, 0.025); }
.accounting-deduction-options >>> .v-label { color: #C8CFDB !important; font-size: 12px !important; }
.accounting-deduction-summary { margin-top: 10px; padding: 10px 12px; border-radius: 11px; background: rgba(88, 216, 141, 0.075); color: #9DA8BA; font-size: 12.5px; }
.accounting-preview-title { display: flex; align-items: center; flex-wrap: wrap; gap: 9px; padding: 15px 18px !important; border-bottom: 1px solid rgba(255, 255, 255, 0.06); }
.accounting-preview-title > div:first-child { min-width: 180px; display: flex; flex: 1; flex-direction: column; }
.accounting-preview-title small { color: #768198; font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; }
.accounting-preview-title strong { color: #FFF; font-size: 15px; }
.accounting-preview-actions { display: flex; gap: 6px; }
.accounting-preview-actions >>> .v-btn { margin: 0; border-radius: 9px; text-transform: none; }
.accounting-preview-dialog .amber.lighten-5 { background: rgba(255, 183, 77, 0.1) !important; color: #FFD79A !important; }
.accounting-simple-table-wrap { width: 100%; overflow-x: auto; border-radius: 10px; }
.accounting-dialog >>> .v-data-table { background: rgba(255, 255, 255, 0.018) !important; color: #E2E6EE !important; }
.accounting-dialog >>> .v-data-table th { color: #78849A !important; white-space: nowrap; }
.accounting-dialog >>> .v-data-table td { color: #D7DCE6 !important; white-space: nowrap; }
.accounting-dialog-hint { margin-bottom: 13px; padding: 10px 11px; border-radius: 10px; background: rgba(84, 120, 255, 0.08); color: #AEB9CE; font-size: 12px; line-height: 1.45; }
.accounting-range-shortcuts { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
.accounting-ksef-filters { max-width: 540px; display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto; align-items: center; gap: 8px; margin-bottom: 14px; }
.accounting-ksef-filters >>> .v-btn { height: 40px; margin: 0; border-radius: 10px; text-transform: none; }

@media (max-width: 1180px) {
  .accounting-header { grid-template-columns: minmax(0, 1fr); }
  .accounting-period-card { min-width: 0; justify-content: flex-start; }
  .accounting-ksef-state { margin-left: auto; }
  .accounting-overview { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 820px) {
  .accounting-page { margin: -16px; padding: 16px 12px 38px; }
  .accounting-period-card { align-items: stretch; flex-wrap: wrap; }
  .accounting-period-copy { flex: 1 1 100%; }
  .accounting-period-fields { flex: 1 1 auto; }
  .accounting-month-select { width: 150px; }
  .accounting-ksef-state { min-height: 40px; }
  .accounting-section-head { flex-direction: column; }
  .accounting-section-actions { width: 100%; justify-content: flex-start; }
  .accounting-toolbar { align-items: stretch; flex-wrap: wrap; }
  .accounting-search { max-width: none; flex: 1 1 100%; }
  .accounting-category-select { max-width: none; flex: 1 1 220px; }
  .accounting-result-count { min-height: 38px; margin-left: 0; }
}

@media (max-width: 700px) {
  .accounting-heading-icon { width: 42px; height: 42px; flex-basis: 42px; border-radius: 13px; }
  .accounting-heading h1 { font-size: 21px; }
  .accounting-heading p { font-size: 12.5px; }
  .accounting-period-fields { width: 100%; }
  .accounting-period-fields > * { flex: 1 1 0; width: auto; }
  .accounting-ksef-state { width: 100%; margin-left: 0; padding: 8px 2px 1px; border-top: 1px solid rgba(255, 255, 255, 0.06); border-left: 0; }
  .accounting-overview { grid-template-columns: minmax(0, 1fr); }
  .accounting-overview-card { padding: 12px; }
  .accounting-tabs-shell { overflow: hidden; }
  .accounting-tabs-shell >>> .v-tabs-bar { height: 46px; }
  .accounting-tabs-shell >>> .v-tab { padding: 0 13px; }
  .accounting-content-panel { padding: 14px 12px; }
  .accounting-section-head h2 { font-size: 16px; }
  .accounting-primary-btn { width: 100%; }
  .accounting-section-actions { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .accounting-section-actions > *:last-child { grid-column: 1 / -1; }
  .accounting-section-actions >>> .v-btn { width: 100%; }
  .accounting-desktop-table { display: none; }
  .accounting-mobile-list { display: grid; gap: 8px; }
  .accounting-result-count { width: 100%; padding-left: 3px; }
  .accounting-summary-grid >>> .v-col { padding: 4px; }
  .accounting-summary-card { min-height: 104px; }
  .accounting-dialog-title { padding: 14px 15px !important; }
  .accounting-dialog >>> .v-card__text { padding: 15px !important; }
  .accounting-deduction-options >>> .v-input--radio-group__input { align-items: stretch; flex-direction: column; }
  .accounting-ksef-filters { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
  .accounting-ksef-filters >>> .v-btn { grid-column: 1 / -1; width: 100%; }
}

@media (max-width: 430px) {
  .accounting-page { padding-inline: 10px; }
  .accounting-header { gap: 14px; }
  .accounting-period-card { padding: 10px; }
  .accounting-tabs-shell >>> .v-tab { padding: 0 10px; font-size: 12px; }
  .accounting-tabs-shell >>> .v-tab .v-icon { margin-right: 5px; }
  .tab-count { margin-left: 5px; }
  .mobile-card-facts { grid-template-columns: minmax(0, 1fr); }
  .mobile-card-actions >>> .v-btn { padding: 0 5px !important; }
  .accounting-preview-title { align-items: flex-start; flex-direction: column; }
  .accounting-preview-actions { width: 100%; }
  .accounting-preview-actions >>> .v-btn { flex: 1 1 0; }
}
</style>
